import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Tag,
  Upload
} from 'antd'
import { FormInstance } from 'antd/lib'

import {
  ExternalEntryDocumentResponse,
  InventoryDocumentState
} from 'src/api/requests'
import { CustomItemRender, requiredRules, Select, Text } from 'src/components'
import { CounterPartSelect } from 'src/features/counterParts/components'
import { fixedDecimal, getEntryStatus } from 'src/features/stock/utils'
import { FileDocument, onDownloadFile } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { ConsumablesTable } from './ConsumablesTable'
import { InputsCommentSection } from './InputsCommentSection'
import { ServicesTable } from './ServicesTable'
import { useStyles } from './styles'

type Props = {
  form: FormInstance
  item?: ExternalEntryDocumentResponse
}

export const InputConfirmedDetails = ({ form, item }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles({ hasId: true })

  const isDeliveryDateDisabled =
    ![InventoryDocumentState.NEW, InventoryDocumentState.WAITING].includes(
      item?.state ?? InventoryDocumentState.NEW
    ) || !item

  const medicineItems = Form.useWatch('medicine', form)
  const servicesItems = Form.useWatch('services', form)

  const totalMedicine = fixedDecimal(
    medicineItems?.reduce(
      (acc: number, item: { priceTotal: string }) =>
        acc + parseFloat(item?.priceTotal ?? 0),
      0
    ) ?? 0,
    4
  )

  const totalServices = fixedDecimal(
    servicesItems?.reduce(
      (acc: number, item: { unitPrice: number }) =>
        acc + (item?.unitPrice ?? 0),
      0
    ) ?? 0,
    4
  )

  const totalAmount = fixedDecimal(
    (totalMedicine ?? 0) + (totalServices ?? 0),
    4
  )

  return (
    <Flex vertical gap={24} className={styles.printPage}>
      <Card className="main-card">
        <Flex vertical gap={24}>
          <Flex justify="space-between" align="center">
            <Text weight={700} size="xxl">
              {t(
                !item
                  ? 'stock_administration.record_incoming_invoice'
                  : 'stock.stock_entry'
              )}
            </Text>
            {!!item && (
              <Tag color={getEntryStatus(t, item?.state).color}>
                {getEntryStatus(t, item?.state).text}
              </Tag>
            )}
          </Flex>

          <Card className={styles.detailsCard}>
            <Flex vertical gap={24}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="invoiceDate"
                    label={t(`stock.invoice_date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="invoiceNumber"
                    label={t(`stock.accompanying_invoice`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled
                      placeholder={t(`stock.accompanying_invoice`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="supplier"
                    label={t(`stock.provider`)}
                    rules={requiredRules}
                  >
                    <Select />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="purchase_center"
                    label={t(`counterparts.purchase_center`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      disabled
                      placeholder={t(`counterparts.purchase_center`)}
                    />
                  </Form.Item>
                </Col>

                {item?.state !== InventoryDocumentState.REJECTED && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="takeoverDate"
                        label={t(`stock_administration.pickup_date`)}
                        rules={requiredRules}
                      >
                        <DatePicker
                          className="full-width"
                          disabled
                          format={dayMonthYearBackSlash}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="deliveryDate"
                        label={t(`stock_administration.delivery_date`)}
                        rules={requiredRules}
                      >
                        <DatePicker
                          className="full-width"
                          disabled={isDeliveryDateDisabled}
                          format={dayMonthYearBackSlash}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
              {item?.state !== InventoryDocumentState.REJECTED && (
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="transporterId"
                      label={t(`stock_administration.select_transport`)}
                      rules={requiredRules}
                    >
                      <CounterPartSelect
                        allowClear={false}
                        placeholder={t(`stock_administration.select_transport`)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="transporterAccompanyingNotice"
                      label={t(`stock_administration.way_bill`)}
                      rules={requiredRules}
                    >
                      <Input
                        className="full-width"
                        placeholder={t(`stock_administration.way_bill`)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Flex>
          </Card>
        </Flex>
      </Card>

      <Card className="secondary-card">
        <Card title={t('stock.stocks')}>
          <Form.List name="medicine">
            {(fields, { add, remove }) => (
              <ConsumablesTable
                item={item}
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>
        </Card>

        <Flex
          justify="end"
          align="center"
          gap={8}
          className="stock-total-price"
        >
          <Text weight={700}>{t('common.total')}: </Text>
          <Text weight={700}>{totalMedicine ?? 0} MDL</Text>
        </Flex>
      </Card>

      {!item?.contract?.isInternal && (
        <Card>
          <Form.List name="services">
            {(fields, { add, remove }) => (
              <ServicesTable
                fields={fields}
                add={add}
                remove={remove}
                form={form}
                item={item}
              />
            )}
          </Form.List>
          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>
              {t('common.total')} ({t('stock.services')}):
            </Text>
            <Text weight={700}>{totalServices ?? 0} MDL</Text>
          </Flex>
          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>
              {t('common.total')} ({t('stock.services_products')}):
            </Text>
            <Text weight={700}>{totalAmount} MDL</Text>
          </Flex>
        </Card>
      )}

      <Card className="secondary-card">
        <Flex vertical gap={24}>
          <Card
            title={t('stock_applications.uploaded_documents')}
            className={styles.extraCardStyles}
          >
            <Upload
              itemRender={(_, file: FileDocument) => (
                <CustomItemRender
                  file={file}
                  onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                />
              )}
              defaultFileList={item?.mediaFiles?.map((file, index) => ({
                ...file,
                uid: String(index),
                name: file?.name || `file-${index}`
              }))}
              disabled
            />
          </Card>
          <InputsCommentSection data={item} />

          {!isDeliveryDateDisabled && (
            <Row className="comment-card">
              <Form.Item
                name="commentNew"
                className="full-width"
                label={t('analysis.cancel_test_comments')}
              >
                <Input.TextArea
                  rows={5}
                  placeholder={t('common.leave_comment')}
                  disabled={isDeliveryDateDisabled}
                />
              </Form.Item>
            </Row>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
